import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";

/**
 * Public Terms of Service page.
 *
 * Play Store, Apple App Store, and most payment processors expect a Terms
 * document at a stable public URL alongside the Privacy Policy. This page
 * mirrors the visual language of /privacy.
 */

const SECTIONS = [
  {
    id: "acceptance",
    title: "1. Acceptance of terms",
    body: (
      <p>
        By creating an Impo account or using any Impo service, you agree to these Terms. If you don't agree, please stop using Impo and delete your account from Profile → Data controls.
      </p>
    ),
  },
  {
    id: "account",
    title: "2. Your account",
    body: (
      <ul className="space-y-2 list-disc pl-5">
        <li>You are responsible for the security of your credentials (email/phone, password, passkeys).</li>
        <li>You must be at least 13 years old to use Impo. If you're in a jurisdiction that requires a higher minimum age, that age applies.</li>
        <li>One person, one account. Sharing credentials with others is not permitted.</li>
        <li>You are responsible for the content you save and for ensuring you have the legal right to save it.</li>
      </ul>
    ),
  },
  {
    id: "use",
    title: "3. Acceptable use",
    body: (
      <>
        <p className="mb-3">You agree not to use Impo to:</p>
        <ul className="space-y-2 list-disc pl-5">
          <li>Store or transmit content that infringes intellectual property, sexually exploits minors, incites violence, or violates applicable law.</li>
          <li>Reverse-engineer, scrape, or attempt to bypass the security of the service.</li>
          <li>Use AI features to generate content that violates the acceptable use policies of our upstream model providers (Anthropic, OpenAI).</li>
          <li>Abuse the service in a way that materially degrades it for others (excessive automated calls, spam, etc.).</li>
        </ul>
      </>
    ),
  },
  {
    id: "subscriptions",
    title: "4. Subscriptions, payments &amp; refunds",
    body: (
      <ul className="space-y-2 list-disc pl-5">
        <li>Free and paid plans are offered as described on the Plan page inside the app.</li>
        <li>Paid subscriptions are billed via Razorpay in your local currency. Recurring subscriptions renew automatically at the end of each billing period unless cancelled beforehand.</li>
        <li>Refunds: any unused pro-rata amount can be refunded within 7 days of the first purchase by writing to hello@impoapp.com. After that window, we do not offer partial refunds for the current cycle.</li>
        <li>Downgrading to a lower plan takes effect at the end of your current billing period. Storage-based limits still apply.</li>
      </ul>
    ),
  },
  {
    id: "content",
    title: "5. Your content, our promise",
    body: (
      <ul className="space-y-2 list-disc pl-5">
        <li>You retain all rights to the content you save on Impo.</li>
        <li>You grant Impo the limited licence needed to store, transmit, and process your content solely to provide the service to you (e.g., render your files, run search, deliver reminders).</li>
        <li>Your content is never used to train third-party AI models. See the Privacy Policy for how AI features work.</li>
      </ul>
    ),
  },
  {
    id: "availability",
    title: "6. Service availability &amp; changes",
    body: (
      <p>
        We work hard to keep Impo reliable, but the service is provided “as is”. We may modify, suspend, or discontinue features with reasonable notice. If we discontinue Impo entirely, we will give you enough time to export your data before shutdown.
      </p>
    ),
  },
  {
    id: "termination",
    title: "7. Termination",
    body: (
      <ul className="space-y-2 list-disc pl-5">
        <li>You can delete your account at any time (Profile → Data controls).</li>
        <li>We may suspend or terminate an account that materially breaches these Terms, with notice where feasible.</li>
        <li>On termination the licence to your content ends and content is deleted according to the retention schedule in the Privacy Policy.</li>
      </ul>
    ),
  },
  {
    id: "liability",
    title: "8. Limitation of liability",
    body: (
      <p>
        To the maximum extent permitted by applicable law, Impo is not liable for indirect, incidental, special, or consequential damages arising from your use of the service. Our aggregate liability for any claim is limited to the fees you paid to Impo in the twelve (12) months preceding the claim, or, if you're on a free plan, INR 1,000 — whichever is greater. Nothing in these Terms limits liability that cannot lawfully be limited (e.g., gross negligence, death or personal injury caused by our negligence).
      </p>
    ),
  },
  {
    id: "law",
    title: "9. Governing law &amp; disputes",
    body: (
      <p>
        These Terms are governed by the laws of India, without regard to conflict-of-laws principles. Any dispute will be resolved in the courts of Bengaluru, Karnataka, unless a mandatory consumer-protection law in your jurisdiction gives you a different forum.
      </p>
    ),
  },
  {
    id: "changes",
    title: "10. Changes to these Terms",
    body: (
      <p>
        We may update these Terms from time to time. If a change is material we will notify you in-app or by email before it takes effect. Continued use after the effective date means you accept the new Terms.
      </p>
    ),
  },
  {
    id: "contact",
    title: "11. Contact",
    body: (
      <p>
        Questions, complaints, or notices under these Terms:{" "}
        <a href="mailto:hello@impoapp.com" className="text-primary underline">hello@impoapp.com</a>.
      </p>
    ),
  },
];

export default function Terms() {
  const nav = useNavigate();
  useEffect(() => { document.title = "Impo — Terms of Service"; }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="terms-page">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b border-border">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} data-testid="terms-back-button" aria-label="Go back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Link to="/" className="flex items-center gap-2" data-testid="terms-logo-link">
            <Logo className="h-6" />
          </Link>
          <div className="ml-auto text-[11px] text-muted-foreground">Legal</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Terms of Service</div>
          <h1 className="font-display text-3xl font-bold leading-tight" data-testid="terms-title">The rules of the road</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            These Terms explain the deal between you and Impo. Please read them — they're written to be read.
          </p>
        </div>

        <nav aria-label="Table of contents" className="rounded-2xl border border-border bg-card p-4" data-testid="terms-toc">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">On this page</div>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-3 text-sm">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-primary hover:underline">
                  {s.title.replace(/&amp;/g, "&")}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-5">
          {SECTIONS.map((s) => (
            <section
              key={s.id}
              id={s.id}
              className="rounded-2xl border border-border bg-card p-5"
              data-testid={`terms-section-${s.id}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
                  <FileText className="h-5 w-5" />
                </div>
                <h2 className="font-display font-semibold text-lg">
                  {s.title.replace(/&amp;/g, "&")}
                </h2>
              </div>
              <div className="text-sm text-foreground/85 leading-relaxed">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        <footer className="pt-4 pb-8 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
          <div>Last updated: 22 July 2026</div>
          <div className="flex items-center gap-3">
            <Link to="/privacy" className="underline" data-testid="terms-footer-privacy">Privacy</Link>
            <Link to="/" className="underline" data-testid="terms-footer-home">Home</Link>
            <a href="mailto:hello@impoapp.com" className="underline">Contact</a>
          </div>
        </footer>
      </main>
    </div>
  );
}
